import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fetchRestaurants } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useLocation as useSavedLocation } from "@/context/LocationContext";
import RestaurantCard from "@/components/RestaurantCard";
import { Input } from "@/components/ui/input";
import { Search, MapPin, ChevronDown, ChevronRight, ChevronLeft, BadgePercent, Flame, Bike, RefreshCw, WifiOff } from "lucide-react";
import { findCity } from "@/data/saudi_locations";

// Resilient local cache: production traffic occasionally hits transient
// Cloudflare 520s on mobile networks. Caching the last good response means the
// app shows content instantly and never goes blank during a flaky request.
const cacheKeyRest = (city) => `jh_cache_rest_${city || "all"}`;
const CACHE_KEY_CATS = "jh_cache_cats";
const readCache = (k) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; }
  catch { return null; }
};
const writeCache = (k, data) => {
  try { localStorage.setItem(k, JSON.stringify(data)); } catch { /* quota — ignore */ }
};

// Promotional banners (carousel)
const BANNERS_AR = [
  { title: "خصم يصل إلى 40%", subtitle: "على أفضل المطاعم لفترة محدودة", bg: "from-[#E05D3A] to-[#C24B2C]", image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800" },
  { title: "توصيل مجاني اليوم", subtitle: "لأول طلب لك، استمتع بوجبتك", bg: "from-[#1A1A1A] to-[#3A3A3A]", image: "https://images.unsplash.com/photo-1550547660-d9450f859349?w=800" },
  { title: "جديد: المطاعم الصحية", subtitle: "اكتشف خيارات جديدة ومغذية", bg: "from-[#00A85A] to-[#007A3D]", image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800" },
];
const BANNERS_EN = [
  { title: "Up to 40% OFF", subtitle: "On top restaurants — limited time", bg: "from-[#E05D3A] to-[#C24B2C]", image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800" },
  { title: "Free delivery today", subtitle: "On your first order", bg: "from-[#1A1A1A] to-[#3A3A3A]", image: "https://images.unsplash.com/photo-1550547660-d9450f859349?w=800" },
  { title: "New: healthy spots", subtitle: "Discover fresh, nutritious meals", bg: "from-[#00A85A] to-[#007A3D]", image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800" },
];

export default function Home() {
  const { t, lang, isRTL } = useLang();
  const { location } = useSavedLocation();
  // Hydrate from the last successful response so the screen is never blank,
  // even if the first network call fails (transient 520) on launch.
  const [restaurants, setRestaurants] = useState(() => readCache(cacheKeyRest(location.city_id)) || []);
  const [categories, setCategories] = useState(() => readCache(CACHE_KEY_CATS) || []);
  // UX state so the content area is never a confusing blank: show a loading
  // skeleton while fetching, and a clear retry message if it ultimately fails.
  const [loading, setLoading] = useState(() => (readCache(cacheKeyRest(location.city_id)) || []).length === 0);
  const [failed, setFailed] = useState(false);
  const reloadRef = useRef(null);
  const [query, setQuery] = useState("");
  const [activeBanner, setActiveBanner] = useState(0);
  const navigate = useNavigate();
  const Chevron = isRTL ? ChevronLeft : ChevronRight;
  const banners = lang === "ar" ? BANNERS_AR : BANNERS_EN;
  const bannerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer;
    // Show this city's cached data immediately while we refresh in the background.
    const cachedR = readCache(cacheKeyRest(location.city_id));
    if (cachedR && cachedR.length) { setRestaurants(cachedR); setLoading(false); }

    const loadOnce = async () => {
      let gotRestaurants = false;
      try {
        const data = await fetchRestaurants(location.city_id);
        if (!cancelled && Array.isArray(data)) {
          setRestaurants(data);
          writeCache(cacheKeyRest(location.city_id), data);
          gotRestaurants = true;
        }
      } catch (e) { /* transient — keep retrying */ }
      try {
        const { data } = await api.get("/categories");
        if (!cancelled && Array.isArray(data) && data.length) {
          setCategories(data);
          writeCache(CACHE_KEY_CATS, data);
        }
      } catch (e) { /* non-fatal */ }
      return gotRestaurants;
    };

    // Production occasionally returns transient Cloudflare 520s on mobile
    // networks. Keep retrying in the background (capped backoff) until we get a
    // good response — so the app self-heals instead of staying empty.
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      const ok = await loadOnce();
      attempts += 1;
      if (ok) { setLoading(false); setFailed(false); return; }
      if (attempts < 5 && !cancelled) {
        timer = setTimeout(tick, Math.min(1200 * attempts, 7000) + Math.random() * 500);
      } else if (!cancelled) {
        // Tried a few times with nothing to show → surface a clear retry UI
        // (~15s). The manual "try again" button restarts the cycle.
        setLoading(false);
        setFailed(true);
      }
    };
    // Expose a manual retry for the "try again" button.
    reloadRef.current = () => {
      attempts = 0;
      setFailed(false);
      setLoading(true);
      tick();
    };
    tick();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [location.city_id]);

  // Auto-rotate banner
  useEffect(() => {
    const timer = setInterval(() => setActiveBanner((i) => (i + 1) % banners.length), 4000);
    return () => clearInterval(timer);
  }, [banners.length]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/restaurants?q=${encodeURIComponent(query)}`);
    else navigate("/restaurants");
  };

  const topRated = [...restaurants].sort((a, b) => b.rating - a.rating).slice(0, 6);
  const freeDelivery = restaurants.filter((r) => r.delivery_fee <= 8).slice(0, 6);
  const popular = restaurants.slice(0, 6);

  // Sort by distance from user — nearest first
  const nearby = useMemo(() => {
    const withDist = restaurants.map((r) => {
      let min = Infinity;
      for (const cid of r.cities || []) {
        const c = findCity(cid);
        if (!c) continue;
        const d = Math.hypot(c.lat - location.lat, c.lng - location.lng);
        if (d < min) min = d;
      }
      return { r, d: min };
    });
    withDist.sort((a, b) => a.d - b.d);
    return withDist.map((x) => x.r);
  }, [restaurants, location.lat, location.lng]);

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      {/* Location bar (app-like) */}
      <section className="bg-white border-b border-[#EAEAEA]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#FFF1EA] flex items-center justify-center flex-shrink-0">
            <MapPin className="w-4 h-4 text-[#E05D3A]" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-[#6B6B6B] leading-none">
              {lang === "ar" ? "التوصيل إلى" : "Deliver to"}
            </p>
            <button onClick={() => navigate("/location")} className="flex items-center gap-1 mt-0.5" data-testid="location-picker">
              <span className="font-bold text-sm text-[#1A1A1A] truncate">
                {lang === "ar"
                  ? `${location.city_ar}${location.street ? "، " + location.street : ""}`
                  : `${location.city_en}${location.street ? ", " + location.street : ""}`}
              </span>
              <ChevronDown className="w-4 h-4 text-[#1A1A1A]" />
            </button>
          </div>
        </div>

        {/* Search bar (always visible) */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-4">
          <form onSubmit={handleSearch} className="flex items-center gap-2 bg-[#F5F5F5] rounded-full px-4 py-2.5 border border-[#EAEAEA]">
            <Search className="w-5 h-5 text-[#6B6B6B] flex-shrink-0" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("searchPlaceholder")}
              className="border-0 focus-visible:ring-0 shadow-none text-sm bg-transparent px-0 h-7"
              data-testid="home-search-input"
            />
          </form>
        </div>
      </section>

      {/* Banner carousel */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pt-4">
        <div
          ref={bannerRef}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory hide-scrollbar -mx-4 px-4 md:-mx-6 md:px-6"
        >
          {banners.map((b, i) => (
            <div
              key={b.title || `banner-${i}`}
              className={`snap-start flex-shrink-0 w-[92%] md:w-[520px] aspect-[16/8] md:aspect-[16/6] rounded-2xl overflow-hidden relative shadow-warm bg-gradient-to-br ${b.bg}`}
              data-testid={`banner-${i}`}
            >
              <img src={b.image} alt="" className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60" />
              <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-end text-white">
                <p className="text-xs uppercase tracking-widest opacity-80">
                  {lang === "ar" ? "عرض" : "Offer"}
                </p>
                <h3 className="font-display text-2xl md:text-3xl font-black leading-tight mt-1">{b.title}</h3>
                <p className="text-sm opacity-90 mt-1 max-w-xs">{b.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Dots */}
        <div className="flex justify-center gap-1.5 mt-3">
          {banners.map((b, i) => (
            <span
              key={b.title || `dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === activeBanner ? "w-5 bg-[#E05D3A]" : "w-1.5 bg-[#EAEAEA]"}`}
            />
          ))}
        </div>
      </section>

      {/* Horizontal categories (Jahez-style circles) */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-base text-[#1A1A1A]">
            {lang === "ar" ? "تصفّح بالصنف" : "Browse by cuisine"}
          </h2>
          <Link to="/restaurants" className="text-xs font-semibold text-[#E05D3A] inline-flex items-center gap-1">
            {lang === "ar" ? "الكل" : "All"}
            <Chevron className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="flex gap-4 overflow-x-auto hide-scrollbar -mx-4 px-4 pb-2">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/restaurants?cuisine=${c.id}`}
              className="flex flex-col items-center gap-2 flex-shrink-0 w-[68px] group"
              data-testid={`category-${c.id}`}
            >
              <div className="w-16 h-16 rounded-full bg-white border border-[#EAEAEA] flex items-center justify-center text-3xl group-hover:border-[#E05D3A] group-hover:shadow-md transition-all">
                {c.icon}
              </div>
              <p className="text-[11px] font-semibold text-[#1A1A1A] text-center truncate w-full">
                {lang === "ar" ? c.name_ar : c.name_en}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {restaurants.length === 0 ? (
        <HomeDataStatus
          loading={loading}
          failed={failed}
          cityName={lang === "ar" ? location.city_ar : location.city_en}
          lang={lang}
          onRetry={() => reloadRef.current && reloadRef.current()}
        />
      ) : (
        <>
          {/* Section: Popular (nearest) */}
          <SectionBlock
            icon={<Flame className="w-[18px] h-[18px] text-[#E05D3A]" />}
            title={lang === "ar" ? "الأكثر طلبًا" : "Most ordered"}
            items={nearby.slice(0, 6)}
            Chevron={Chevron}
          />

          {/* Section: Free delivery */}
          {freeDelivery.length > 0 && (
            <SectionBlock
              icon={<Bike className="w-[18px] h-[18px] text-[#00A85A]" />}
              title={lang === "ar" ? "توصيل مجاني" : "Free delivery"}
              items={freeDelivery}
              Chevron={Chevron}
            />
          )}

          {/* Section: Top rated */}
          <SectionBlock
            icon={<BadgePercent className="w-[18px] h-[18px] text-[#E05D3A]" />}
            title={lang === "ar" ? "الأعلى تقييمًا" : "Top rated"}
            items={topRated}
            Chevron={Chevron}
          />

          {/* Section: All restaurants sorted by distance (Jahez-style list) */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-8 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-[18px] h-[18px] text-[#E05D3A]" />
                <h2 className="font-bold text-lg text-[#1A1A1A]">
                  {lang === "ar" ? "الأقرب لك" : "Nearest to you"}
                </h2>
              </div>
            </div>
            <div className="space-y-3">
              {nearby.map((r) => (
                <RestaurantCard key={r.id} r={r} />
              ))}
            </div>
          </section>
        </>
      )}

      <div className="h-10" />

      {/* Floating "Map" button — opens the restaurants map */}
      <button
        onClick={() => navigate("/map")}
        className="fixed z-30 bottom-32 md:bottom-8 end-4 md:end-8 bg-[#1A1A1A] text-white px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 font-bold text-[13px] hover:bg-[#E05D3A] active:scale-[0.98] transition"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 7rem)' }}
        data-testid="open-map-btn"
      >
        <MapPin className="w-4 h-4" />
        {lang === "ar" ? "خريطة" : "Map"}
      </button>
    </div>
  );
}

function HomeDataStatus({ loading, failed, cityName, lang, onRetry }) {
  if (loading) {
    // Skeleton placeholders — the content area is never a blank white space.
    return (
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-8 space-y-3" data-testid="home-loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-2xl border border-[#EEE] p-3 flex items-center gap-3 animate-pulse">
            <div className="w-16 h-16 rounded-xl bg-[#EDEDED] flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-1/2 rounded bg-[#EDEDED]" />
              <div className="h-3 w-1/3 rounded bg-[#F0F0F0]" />
              <div className="h-3 w-2/3 rounded bg-[#F4F4F4]" />
            </div>
          </div>
        ))}
      </section>
    );
  }

  if (failed) {
    return (
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10 mb-10" data-testid="home-error">
        <div className="bg-white rounded-3xl border border-[#EEE] px-6 py-12 text-center flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-[#FFF1EA] flex items-center justify-center mb-4">
            <WifiOff className="w-7 h-7 text-[#E05D3A]" />
          </div>
          <h3 className="font-bold text-lg text-[#1A1A1A]">
            {lang === "ar" ? "تعذّر تحميل المطاعم" : "Couldn't load restaurants"}
          </h3>
          <p className="text-sm text-[#6B6B6B] mt-1.5 max-w-xs">
            {lang === "ar"
              ? "تحقّق من اتصالك بالإنترنت وحاول مرة أخرى."
              : "Please check your connection and try again."}
          </p>
          <button
            onClick={onRetry}
            data-testid="home-retry-btn"
            className="mt-5 inline-flex items-center gap-2 bg-[#E05D3A] hover:bg-[#C24B2C] text-white font-bold text-sm rounded-full px-7 py-3 active:scale-[0.98] transition"
          >
            <RefreshCw className="w-4 h-4" />
            {lang === "ar" ? "إعادة المحاولة" : "Try again"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10 mb-10" data-testid="home-no-coverage">
      <div className="bg-white rounded-3xl border border-[#EEE] px-6 py-12 text-center flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-[#FFF1EA] flex items-center justify-center mb-4">
          <MapPin className="w-7 h-7 text-[#E05D3A]" />
        </div>
        <h3 className="font-bold text-lg text-[#1A1A1A]">
          {lang === "ar" ? `لم نصل إلى ${cityName} بعد` : `We haven't reached ${cityName} yet`}
        </h3>
        <p className="text-sm text-[#6B6B6B] mt-1.5 max-w-xs">
          {lang === "ar"
            ? "جايك متاح حالياً في مدن محددة، ونعمل على التوسع قريباً."
            : "Jayek is currently available in selected cities. We're expanding soon."}
        </p>
      </div>
    </section>
  );
}

function SectionBlock({ icon, title, items, Chevron }) {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 mt-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="font-bold text-lg text-[#1A1A1A]">{title}</h2>
        </div>
        <Link to="/restaurants" className="text-xs font-semibold text-[#E05D3A] inline-flex items-center gap-1">
          <Chevron className="w-3.5 h-3.5" />
        </Link>
      </div>
      {/* Vertical list (Jahez-style) */}
      <div className="space-y-3">
        {items.map((r) => <RestaurantCard key={r.id} r={r} />)}
      </div>
    </section>
  );
}
